/**
 * Centralized Design Token System
 *
 * Single source of truth for all spacing, sizing, and layout tokens
 * used across renderers. Eliminates duplication of token maps
 * in element-renderer, badge-renderer, container-renderer, card-renderer, etc.
 */

/**
 * Semantic spacing tokens → Tailwind spacing scale values.
 * Used for gap, padding, margin, and general spacing properties.
 *
 * Mapping: xs=1 (4px), sm=2 (8px), md=4 (16px), lg=6 (24px), xl=8 (32px)
 */
const SPACING_SCALE: Record<string, string> = {
  xs: "1",
  sm: "2",
  md: "4",
  lg: "6",
  xl: "8",
}

/**
 * Position offset tokens → Tailwind spacing scale values.
 * Used for absolute/fixed position offsets (top, right, bottom, left).
 * Slightly larger than SPACING_SCALE for visual clarity of positioned elements.
 *
 * Mapping: xs=1.5 (6px), sm=3 (12px), md=4 (16px), lg=6 (24px), xl=8 (32px)
 */
const POSITION_SCALE: Record<string, string> = {
  xs: "1.5",
  sm: "3",
  md: "4",
  lg: "6",
  xl: "8",
}

/**
 * Resolve a spacing token (xs/sm/md/lg/xl) or raw value to a Tailwind spacing class value.
 * Used for gap, padding, margin.
 *
 * @example spacingToken("md") → "4"
 * @example spacingToken("16") → "16"
 * @example spacingToken(4) → "4"
 */
export function spacingToken(token: string | number): string {
  if (typeof token === "number") return String(token)
  if (/^\d+(\.\d+)?$/.test(token)) return token
  // Convert rem values to Tailwind spacing scale (1rem = 4 units)
  const remMatch = /^(\d+(?:\.\d+)?)rem$/.exec(String(token))
  if (remMatch) {
    const remVal = parseFloat(remMatch[1])
    const twVal = remVal * 4
    // Use integer if whole number, otherwise keep decimal
    return twVal % 1 === 0 ? String(twVal) : String(twVal)
  }
  return SPACING_SCALE[token] ?? token
}

/**
 * Resolve a position offset token to a Tailwind spacing class value.
 * Used for top/right/bottom/left in absolute/fixed positioning.
 *
 * @example positionToken("sm") → "3"
 * @example positionToken("1.5") → "1.5"
 * @example positionToken("-1") → "-1"
 */
export function positionToken(token: string): string {
  return POSITION_SCALE[token] ?? token
}
